import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useJsApiLoader, GoogleMap, Marker, Polyline, InfoWindow, Circle } from '@react-google-maps/api';
import { Maximize2, Minimize2, Crosshair } from 'lucide-react';
import { googleMapsLoaderConfig } from '../../config/googleMapsConfig';
import {
  getDirectionalMarkers,
  calculateRotation,
} from '../../utils/mapMarkers';
import pickupPng from '../../svg/pickup.png';
import hospitalPng from '../../svg/hospital.png';
import ambulancePng from '../../svg/Rectangle.png';
import { uberMapStyle } from '../../styles/mapStyle';
import { computeRoute } from '../../utils/routesApi';
import {
  isDriverDeviated,
  resolveTargetDestination,
  calculateDistanceMeters,
  DEFAULT_DEVIATION_THRESHOLD_METERS,
  DEFAULT_RECALCULATION_COOLDOWN_MS,
  ARRIVAL_THRESHOLD_METERS,
} from '../../utils/routeDeviation';
import PremiumMapLegend from './PremiumMapLegend';

// Use shared loader configuration to ensure consistency across all components
const loaderConfig = {
  ...googleMapsLoaderConfig,
  // Removed preventGoogleFontsLoading to avoid recursive loop when multiple map instances exist
};

// Suppress Google Maps authentication failure dialogs globally
if (typeof window !== 'undefined' && !window.gm_authFailure) {
  window.gm_authFailure = function() {
    // Suppress the authentication failure dialog - map will still attempt to render
    // This prevents the "do you own this website?" error dialog
  };
}

/**
 * TrackingMap Component
 * Displays live ambulance tracking with pickup, dropoff, and ambulance position
 */
const TrackingMap = ({
  pickupLocation = null,
  dropoffLocation = null,
  ambulanceLocation = null,
  distance = null,
  eta = null,
  status = null,
  journeyDirection = 'OUTBOUND',
  zoom = 14,
  showControls = false,
  height = '100%',
}) => {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const [map, setMap] = useState(null);
  const [routePath, setRoutePath] = useState([]); // Route from pickup to dropoff
  const [plannedRoutePath, setPlannedRoutePath] = useState([]); // Original planned route (RED)
  const [routeVersion, setRouteVersion] = useState(0);
  const lastRecalculationRef = useRef(0);
  const isRecalculatingRef = useRef(false);
  const [driverToPickupRoute, setDriverToPickupRoute] = useState([]); // Route from driver to pickup
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mapCenter, setMapCenter] = useState({ lat: 40.7128, lng: -74.0060 }); // Default to NYC (from image)
  const [selectedMarker, setSelectedMarker] = useState(null);
  const previousLocationRef = useRef(null);
  const lastRotationRef = useRef(0);
  const [lastLocationUpdate, setLastLocationUpdate] = useState(null);
  const [locationJustUpdated, setLocationJustUpdated] = useState(false);
  const previousAmbulanceLocationRef = useRef(null);
  const [userInteracting, setUserInteracting] = useState(false);
  const userInteractionTimeoutRef = useRef(null);
  const driverMarkerRef = useRef(null);
  const pickupMarkerRef = useRef(null);
  const dropoffMarkerRef = useRef(null);
  const initialBoundsFittedRef = useRef(false);
  const isProgrammaticMoveRef = useRef(false);

  // Ensure libraries array is always included and consistent
  const { isLoaded } = useJsApiLoader({
    id: loaderConfig.id,
    googleMapsApiKey: loaderConfig.googleMapsApiKey,
    libraries: loaderConfig.libraries, // Explicitly use libraries from shared config
    onError: (error) => {
      // Suppress error dialog - just log it silently
      // Don't set error state to allow map to still attempt rendering
    }
  });

  // Debug logging removed to improve performance

  const onMapLoad = useCallback((mapInstance) => {
    try {
      mapRef.current = mapInstance;
      setMap(mapInstance);
    } catch (error) {
      // Silently handle errors
    }
  }, []);

  // Imperative pickup/dropoff markers — native google.maps.Marker for guaranteed rendering
  useEffect(() => {
    if (!map || !window.google?.maps) {
      return;
    }

    const extractPos = (loc) => {
      if (!loc) return null;
      const lat = parseFloat(loc.latitude ?? loc.lat);
      const lng = parseFloat(loc.longitude ?? loc.lng ?? loc.lon);
      if (isNaN(lat) || isNaN(lng)) return null;
      return { lat, lng };
    };

    const isInbound = journeyDirection?.toUpperCase() === 'INBOUND';
    const pUrl = isInbound ? hospitalPng : pickupPng;
    const dUrl = isInbound ? pickupPng : hospitalPng;

    const pickupPos = extractPos(pickupLocation);
    const dropoffPos = extractPos(dropoffLocation);

    if (pickupPos) {
      if (!pickupMarkerRef.current) {
        pickupMarkerRef.current = new window.google.maps.Marker({
          map,
          position: pickupPos,
          title: 'Pickup Location',
          icon: { url: pUrl, scaledSize: new window.google.maps.Size(56, 56), anchor: new window.google.maps.Point(28, 56) },
          zIndex: 1008,
          optimized: false,
        });
      } else {
        pickupMarkerRef.current.setPosition(pickupPos);
        pickupMarkerRef.current.setIcon({ url: pUrl, scaledSize: new window.google.maps.Size(56, 56), anchor: new window.google.maps.Point(28, 56) });
        pickupMarkerRef.current.setMap(map);
      }
    } else {
      pickupMarkerRef.current?.setMap(null);
      pickupMarkerRef.current = null;
    }

    if (dropoffPos) {
      if (!dropoffMarkerRef.current) {
        dropoffMarkerRef.current = new window.google.maps.Marker({
          map,
          position: dropoffPos,
          title: 'Dropoff Location',
          icon: { url: dUrl, scaledSize: new window.google.maps.Size(60, 60), anchor: new window.google.maps.Point(30, 60) },
          zIndex: 1006,
          optimized: false,
        });
      } else {
        dropoffMarkerRef.current.setPosition(dropoffPos);
        dropoffMarkerRef.current.setIcon({ url: dUrl, scaledSize: new window.google.maps.Size(60, 60), anchor: new window.google.maps.Point(30, 60) });
        dropoffMarkerRef.current.setMap(map);
      }
    } else {
      dropoffMarkerRef.current?.setMap(null);
      dropoffMarkerRef.current = null;
    }

    return () => {
      pickupMarkerRef.current?.setMap(null);
      dropoffMarkerRef.current?.setMap(null);
      pickupMarkerRef.current = null;
      dropoffMarkerRef.current = null;
    };
  }, [map, pickupLocation, dropoffLocation, journeyDirection]);

  // Handle user map interaction - when user starts dragging/panning
  const handleMapDragStart = useCallback(() => {
    setUserInteracting(true);
    // Clear any existing timeout
    if (userInteractionTimeoutRef.current) {
      clearTimeout(userInteractionTimeoutRef.current);
      userInteractionTimeoutRef.current = null;
    }
  }, []);

  // Handle user map interaction end - do NOT auto-reset after 5s!
  // Operator stays in control until they explicitly click the Recenter button
  const handleMapDragEnd = useCallback(() => {
    setUserInteracting(true);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (userInteractionTimeoutRef.current) {
        clearTimeout(userInteractionTimeoutRef.current);
      }
    };
  }, []);

  // Reset user interaction state when exiting fullscreen
  useEffect(() => {
    return () => {
      if (userInteractionTimeoutRef.current) {
        clearTimeout(userInteractionTimeoutRef.current);
      }
    };
  }, [isFullscreen]);

  // Calculate route from driver to pickup using Routes API
  const driverToPickupCalculationInProgress = useRef(false);
  const currentDriverToPickupKeyRef = useRef(null);

  const calculateDriverToPickupRoute = useCallback(async () => {
    if (!ambulanceLocation || !pickupLocation) {
      setDriverToPickupRoute([]);
      driverToPickupCalculationInProgress.current = false;
      currentDriverToPickupKeyRef.current = null;
      return;
    }

    const thisRouteKey = `${ambulanceLocation.latitude}-${ambulanceLocation.longitude}-${pickupLocation.latitude}-${pickupLocation.longitude}`;

    if (driverToPickupCalculationInProgress.current) return;
    if (currentDriverToPickupKeyRef.current === thisRouteKey) return;

    driverToPickupCalculationInProgress.current = true;
    currentDriverToPickupKeyRef.current = thisRouteKey;
    setDriverToPickupRoute([]);

    const straightLine = [
      { lat: ambulanceLocation.latitude, lng: ambulanceLocation.longitude },
      { lat: pickupLocation.latitude, lng: pickupLocation.longitude },
    ];

    try {
      const { coordinates } = await computeRoute(
        { lat: ambulanceLocation.latitude, lng: ambulanceLocation.longitude },
        { lat: pickupLocation.latitude, lng: pickupLocation.longitude }
      );

      driverToPickupCalculationInProgress.current = false;
      if (currentDriverToPickupKeyRef.current !== thisRouteKey) return;

      if (coordinates.length > 0) {
        setDriverToPickupRoute(coordinates);
      } else {
        setDriverToPickupRoute(straightLine);
        currentDriverToPickupKeyRef.current = null;
      }
    } catch {
      driverToPickupCalculationInProgress.current = false;
      setDriverToPickupRoute(straightLine);
      currentDriverToPickupKeyRef.current = null;
    }
  }, [ambulanceLocation, pickupLocation]);

  // Calculate route between pickup and dropoff (like Uber/Zomato)
  // Route calculation using Routes API (v2)
  const routeCalculationInProgress = useRef(false);
  const currentRouteKeyRef = useRef(null);

  const calculateRoute = useCallback(async () => {
    if (!pickupLocation || !dropoffLocation) {
      setRoutePath([]);
      routeCalculationInProgress.current = false;
      currentRouteKeyRef.current = null;
      return;
    }

    const thisRouteKey = `${pickupLocation.latitude}-${pickupLocation.longitude}-${dropoffLocation.latitude}-${dropoffLocation.longitude}`;

    if (routeCalculationInProgress.current) return;
    if (currentRouteKeyRef.current === thisRouteKey) return;

    routeCalculationInProgress.current = true;
    currentRouteKeyRef.current = thisRouteKey;
    setRoutePath([]);

    const straightLine = [
      { lat: pickupLocation.latitude, lng: pickupLocation.longitude },
      { lat: dropoffLocation.latitude, lng: dropoffLocation.longitude },
    ];

    try {
      const { coordinates } = await computeRoute(
        { lat: pickupLocation.latitude, lng: pickupLocation.longitude },
        { lat: dropoffLocation.latitude, lng: dropoffLocation.longitude }
      );

      routeCalculationInProgress.current = false;
      if (currentRouteKeyRef.current !== thisRouteKey) return;

      if (coordinates.length > 0) {
        setRoutePath(coordinates);
        setPlannedRoutePath(coordinates);
      } else {
        setRoutePath(straightLine);
        setPlannedRoutePath(straightLine);
        currentRouteKeyRef.current = null;
      }
    } catch {
      routeCalculationInProgress.current = false;
      setRoutePath(straightLine);
      setPlannedRoutePath(straightLine);
      currentRouteKeyRef.current = null;
    }
  }, [pickupLocation, dropoffLocation]);

  // Update map center and bounds to fit pickup, dropoff, and driver locations (like Uber/Zomato)
  // SKIP bounds update when in fullscreen to keep map stable - only markers will update
  const updateMapBounds = useCallback(() => {
    if (!map || !window.google) return;
    
    // When in fullscreen mode: DO NOT update map bounds - keep map stable
    // Only the marker positions will update, not the entire map
    if (isFullscreen) {
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    const locations = [];

    // Helper function to validate and add location to bounds
    const addLocationToBounds = (location, name) => {
      if (!location || location.latitude == null || location.longitude == null) {
        return false;
      }

      const lat = Number(location.latitude);
      const lng = Number(location.longitude);

      if (isNaN(lat) || isNaN(lng) || !isFinite(lat) || !isFinite(lng)) {
        return false;
      }

      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        return false;
      }

      bounds.extend(new window.google.maps.LatLng(lat, lng));
      locations.push({ lat, lng, name });
      return true;
    };

    // Add all available locations to bounds
    const hasDriver = addLocationToBounds(ambulanceLocation, 'Driver');
    const hasPickup = addLocationToBounds(pickupLocation, 'Pickup');
    const hasDropoff = addLocationToBounds(dropoffLocation, 'Dropoff');

    // Add driver-to-pickup route path points to bounds
    if (driverToPickupRoute && driverToPickupRoute.length > 0) {
      driverToPickupRoute.forEach((point) => {
        if (point && point.lat != null && point.lng != null) {
          const lat = Number(point.lat);
          const lng = Number(point.lng);
          if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng)) {
            bounds.extend(new window.google.maps.LatLng(lat, lng));
          }
        }
      });
    }

    // Add route path points to bounds to ensure full route is visible
    if (routePath && routePath.length > 0) {
      routePath.forEach((point, index) => {
        if (point && point.lat != null && point.lng != null) {
          const lat = Number(point.lat);
          const lng = Number(point.lng);
          if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng)) {
            bounds.extend(new window.google.maps.LatLng(lat, lng));
          }
        }
      });
    }

    // Calculate center based on available locations
    if (locations.length > 0) {
      if (hasDriver && hasPickup && hasDropoff) {
        // All three locations: weight driver more heavily for better tracking visibility
        const driverLoc = locations.find(l => l.name === 'Driver');
        const pickupLoc = locations.find(l => l.name === 'Pickup');
        const dropoffLoc = locations.find(l => l.name === 'Dropoff');
        
        const centerLat = (driverLoc.lat * 0.4 + pickupLoc.lat * 0.3 + dropoffLoc.lat * 0.3);
        const centerLng = (driverLoc.lng * 0.4 + pickupLoc.lng * 0.3 + dropoffLoc.lng * 0.3);
        setMapCenter({ lat: centerLat, lng: centerLng });
      } else if (hasPickup && hasDropoff) {
        // Only pickup and dropoff
        const pickupLoc = locations.find(l => l.name === 'Pickup');
        const dropoffLoc = locations.find(l => l.name === 'Dropoff');
        const centerLat = (pickupLoc.lat + dropoffLoc.lat) / 2;
        const centerLng = (pickupLoc.lng + dropoffLoc.lng) / 2;
        setMapCenter({ lat: centerLat, lng: centerLng });
      } else if (locations.length === 1) {
        // Single location
        const loc = locations[0];
        setMapCenter({ lat: loc.lat, lng: loc.lng });
      } else {
        // Multiple locations - calculate average
        const avgLat = locations.reduce((sum, l) => sum + l.lat, 0) / locations.length;
        const avgLng = locations.reduce((sum, l) => sum + l.lng, 0) / locations.length;
        setMapCenter({ lat: avgLat, lng: avgLng });
      }

      // Fit bounds to show all locations with padding ONLY on initial load or when not interacting
      if (bounds.getNorthEast()) {
        if (!initialBoundsFittedRef.current && !userInteracting) {
          const padding = 120;
          isProgrammaticMoveRef.current = true;
          map.fitBounds(bounds, { padding });
          initialBoundsFittedRef.current = true;
          setTimeout(() => {
            isProgrammaticMoveRef.current = false;
          }, 800);
        }
      } else {
        // Fallback: set center and zoom
        if (!initialBoundsFittedRef.current && !userInteracting) {
          const firstLoc = locations[0];
          map.setCenter(new window.google.maps.LatLng(firstLoc.lat, firstLoc.lng));
          map.setZoom(zoom);
          initialBoundsFittedRef.current = true;
        }
      }
    }
  }, [map, pickupLocation, dropoffLocation, ambulanceLocation, zoom, routePath, driverToPickupRoute, isFullscreen]);

  // Calculate route from driver to pickup when locations change
  const driverToPickupKey = ambulanceLocation && pickupLocation 
    ? `${ambulanceLocation.latitude}-${ambulanceLocation.longitude}-${pickupLocation.latitude}-${pickupLocation.longitude}` 
    : null;
  
  useEffect(() => {
    setDriverToPickupRoute([]);
    
    if (isLoaded && ambulanceLocation && pickupLocation && driverToPickupKey) {
      const frameId = requestAnimationFrame(() => {
        calculateDriverToPickupRoute();
      });
      
      return () => {
        cancelAnimationFrame(frameId);
        setDriverToPickupRoute([]);
      };
    } else {
      setDriverToPickupRoute([]);
    }
  }, [isLoaded, driverToPickupKey, calculateDriverToPickupRoute]);

  // Calculate route when locations change (pickup to dropoff)
  // Use a stable reference to prevent multiple calculations
  const pickupLocationKey = pickupLocation ? `${pickupLocation.latitude}-${pickupLocation.longitude}` : null;
  const dropoffLocationKey = dropoffLocation ? `${dropoffLocation.latitude}-${dropoffLocation.longitude}` : null;
  const routeKey = pickupLocationKey && dropoffLocationKey ? `${pickupLocationKey}-${dropoffLocationKey}` : null;
  
  useEffect(() => {
    setRoutePath([]);

    if (isLoaded && pickupLocation && dropoffLocation && routeKey) {
      calculateRoute();

      return () => setRoutePath([]);
    }
  }, [isLoaded, routeKey, calculateRoute]);

  // Dynamic Route Deviation Detection & Recalculation:
  // When driver deviates from original route (taking a shortcut or alternative path),
  // recalculate the route from current ambulance position to target destination.
  useEffect(() => {
    if (!ambulanceLocation || !routePath || routePath.length < 2) return;

    const target = resolveTargetDestination(status, pickupLocation, dropoffLocation);
    if (!target) return;

    const ambLat = Number(ambulanceLocation.latitude ?? ambulanceLocation.lat);
    const ambLng = Number(ambulanceLocation.longitude ?? ambulanceLocation.lng ?? ambulanceLocation.lon);
    if (!isFinite(ambLat) || !isFinite(ambLng)) return;

    // Skip recalculation if already arrived within arrival threshold
    const distToTarget = calculateDistanceMeters(ambLat, ambLng, target.lat, target.lng);
    if (distToTarget <= ARRIVAL_THRESHOLD_METERS) return;

    const deviated = isDriverDeviated(
      { lat: ambLat, lng: ambLng, accuracy: ambulanceLocation.accuracy },
      routePath,
      DEFAULT_DEVIATION_THRESHOLD_METERS
    );

    if (!deviated) return;

    const now = Date.now();
    if (now - lastRecalculationRef.current < DEFAULT_RECALCULATION_COOLDOWN_MS) return;
    if (isRecalculatingRef.current) return;

    console.log('🔄 [TrackingMap] Route deviation detected — recalculating route from current ambulance location...');
    isRecalculatingRef.current = true;
    lastRecalculationRef.current = now;

    const origin = { lat: ambLat, lng: ambLng };

    computeRoute(origin, target)
      .then(({ coordinates }) => {
        if (coordinates && coordinates.length > 0) {
          console.log(`✅ [TrackingMap] Rerouted successfully! New path has ${coordinates.length} points.`);
          setRoutePath(coordinates);
          setRouteVersion((v) => v + 1);
        }
      })
      .catch((err) => {
        console.warn('⚠️ [TrackingMap] Failed to recalculate route:', err);
      })
      .finally(() => {
        isRecalculatingRef.current = false;
      });
  }, [ambulanceLocation, routePath, status, pickupLocation, dropoffLocation]);

  // Update map bounds when locations change
  // SKIP bounds update when in fullscreen to keep map stable - only markers will update
  useEffect(() => {
    if (isLoaded && map && !isFullscreen) {
      updateMapBounds();
    }
  }, [isLoaded, map, pickupLocation, dropoffLocation, ambulanceLocation, updateMapBounds, isFullscreen]);

  // Real-time update: Update ambulance position smoothly when it changes (Zomato-style)
  // Ensures all three locations (driver, pickup, dropoff) remain visible
  // Detect when driver location changes for visual feedback
  useEffect(() => {
    if (map && ambulanceLocation && ambulanceLocation.latitude != null && ambulanceLocation.longitude != null && window.google && window.google.maps && window.google.maps.geometry) {
      const driverLat = Number(ambulanceLocation.latitude);
      const driverLng = Number(ambulanceLocation.longitude);
      
      if (isNaN(driverLat) || isNaN(driverLng) || !isFinite(driverLat) || !isFinite(driverLng)) {
        return;
      }
      
      // Detect if location actually changed (to trigger visual feedback)
      const locationChanged = !previousAmbulanceLocationRef.current || 
        previousAmbulanceLocationRef.current.latitude !== driverLat ||
        previousAmbulanceLocationRef.current.longitude !== driverLng;
      
      if (locationChanged) {
        setLocationJustUpdated(true);
        // Reset visual indicator after 3 seconds
        setTimeout(() => setLocationJustUpdated(false), 3000);
        
        // Store current location for next comparison
        previousAmbulanceLocationRef.current = {
          latitude: driverLat,
          longitude: driverLng
        };
      }
      
      const newPosition = new window.google.maps.LatLng(driverLat, driverLng);
      
      // Update last location timestamp for visual feedback
      setLastLocationUpdate(new Date());
      
      // When in fullscreen mode: DO NOT update map bounds - keep map stable
      // Only the marker position will update, not the entire map
      // When NOT in fullscreen: Update map bounds to include all three locations
      if (mapRef.current && !isFullscreen) {
        const bounds = new window.google.maps.LatLngBounds();
        
        // Always include driver location (highest priority for real-time tracking)
        bounds.extend(newPosition);
        
        // Include pickup location if available
        if (pickupLocation && pickupLocation.latitude != null && pickupLocation.longitude != null) {
          bounds.extend(new window.google.maps.LatLng(pickupLocation.latitude, pickupLocation.longitude));
        }
        
        // Include dropoff location if available
        if (dropoffLocation && dropoffLocation.latitude != null && dropoffLocation.longitude != null) {
          bounds.extend(new window.google.maps.LatLng(dropoffLocation.latitude, dropoffLocation.longitude));
        }
        
        // Fit bounds with padding to ensure all markers are fully visible
        if (bounds.getNorthEast()) {
          const padding = 120;
          mapRef.current.fitBounds(bounds, { padding });
        }
      }
    }
  }, [map, ambulanceLocation, pickupLocation, dropoffLocation, isFullscreen]);

  // Rotate the ambulance PNG marker via CSS transform.
  // Google Maps Icon objects don't support `rotation` for image-based icons,
  // so we find the marker's <img> DOM element and apply a CSS transform.
  // The ambulance PNG faces LEFT (~270°), so we add 90° to convert compass heading to CSS rotation.
  useEffect(() => {
    if (!ambulanceLocation || !mapContainerRef.current) return;

    const currentLat = Number(ambulanceLocation.latitude);
    const currentLng = Number(ambulanceLocation.longitude);
    if (isNaN(currentLat) || isNaN(currentLng)) return;

    let heading = null;

    if (ambulanceLocation.heading != null && ambulanceLocation.heading !== 0) {
      heading = Number(ambulanceLocation.heading);
    } else if (ambulanceLocation.bearing != null && ambulanceLocation.bearing !== 0) {
      heading = Number(ambulanceLocation.bearing);
    } else if (previousLocationRef.current) {
      const prev = previousLocationRef.current;
      const dist = Math.abs(currentLat - prev.latitude) + Math.abs(currentLng - prev.longitude);
      if (dist > 0.00001) {
        heading = calculateRotation(prev, { latitude: currentLat, longitude: currentLng });
      }
    }

    previousLocationRef.current = { latitude: currentLat, longitude: currentLng };

    const cssRotation = heading != null ? heading + 90 : lastRotationRef.current;
    lastRotationRef.current = cssRotation;

    // Find ambulance <img> by matching against the imported PNG URL (handles both
    // webpack file paths and base64-inlined data URIs).
    const imgs = mapContainerRef.current.querySelectorAll('img');
    for (const img of imgs) {
      if (!img.src) continue;
      const isAmbulance = img.src.includes('Rectangle') || img.src === ambulancePng;
      if (isAmbulance) {
        // Preserve any existing transforms Google Maps applies for positioning
        const existing = img.style.transform || '';
        const stripped = existing.replace(/rotate\([^)]*\)/g, '').trim();
        img.style.transform = stripped
          ? `${stripped} rotate(${cssRotation}deg)`
          : `rotate(${cssRotation}deg)`;
        img.style.transition = 'transform 0.8s ease';
        break;
      }
    }
  }, [ambulanceLocation]);

  // Determine map center based on available locations
  // SKIP center update when in fullscreen to keep map stable
  useEffect(() => {
    if (isFullscreen) {
      // In fullscreen, don't change map center - keep it stable
      return;
    }
    
    if (ambulanceLocation) {
      setMapCenter({ lat: ambulanceLocation.latitude, lng: ambulanceLocation.longitude });
    } else if (pickupLocation) {
      setMapCenter({ lat: pickupLocation.latitude, lng: pickupLocation.longitude });
    } else if (dropoffLocation) {
      setMapCenter({ lat: dropoffLocation.latitude, lng: dropoffLocation.longitude });
    }
  }, [ambulanceLocation, pickupLocation, dropoffLocation, isFullscreen]);

  // Update the "seconds ago" counter every second for real-time feedback
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    if (!ambulanceLocation || !lastLocationUpdate) return;
    
    const interval = setInterval(() => {
      forceUpdate(prev => prev + 1); // Force re-render to update "seconds ago"
    }, 1000);
    
    return () => clearInterval(interval);
  }, [ambulanceLocation, lastLocationUpdate]);


  // Suppress Google Maps error overlays and dialogs
  useEffect(() => {
    // Inject CSS to hide Google Maps error overlays
    const style = document.createElement('style');
    style.id = 'google-maps-error-suppress';
    style.textContent = `
      /* Hide Google Maps error overlays and dialogs */
      .gm-style-cc,
      .gm-err-container,
      .gm-err-message,
      .gm-err-title,
      div[style*="position: fixed"][style*="z-index"][style*="background"],
      div[style*="position: absolute"][style*="z-index"][style*="background"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    `;
    
    if (!document.getElementById('google-maps-error-suppress')) {
      document.head.appendChild(style);
    }
    
    // Use MutationObserver to remove error dialogs as they appear
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check for error messages
            const text = node.textContent || '';
            if (text.includes("can't load") || 
                text.includes("Google Maps") && text.includes("error") ||
                text.includes("do you own this website")) {
              node.style.display = 'none';
              node.style.visibility = 'hidden';
              node.style.opacity = '0';
              node.style.pointerEvents = 'none';
            }
            
            // Check for error classes
            if (node.classList && (
              node.classList.contains('gm-err-container') ||
              node.classList.contains('gm-err-message') ||
              node.classList.contains('gm-err-title')
            )) {
              node.style.display = 'none';
            }
            
            // Recursively check child elements
            const errorElements = node.querySelectorAll ? node.querySelectorAll('*') : [];
            errorElements.forEach((el) => {
              const elText = el.textContent || '';
              if (elText.includes("can't load") || 
                  (elText.includes("Google Maps") && elText.includes("error"))) {
                el.style.display = 'none';
                el.style.visibility = 'hidden';
                el.style.opacity = '0';
                el.style.pointerEvents = 'none';
              }
            });
          }
        });
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    return () => {
      observer.disconnect();
      const styleEl = document.getElementById('google-maps-error-suppress');
      if (styleEl) {
        document.head.removeChild(styleEl);
      }
    };
  }, []);

  // Custom fullscreen toggle that includes all overlays
  const toggleFullscreen = useCallback(() => {
    if (!mapContainerRef.current) return;

    if (!document.fullscreenElement) {
      // Enter fullscreen
      mapContainerRef.current.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        console.error('Error attempting to enable fullscreen:', err);
      });
    } else {
      // Exit fullscreen
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch(err => {
        console.error('Error attempting to exit fullscreen:', err);
      });
    }
  }, []);

  // Recenter map to show all markers (pickup, dropoff, driver)
  const recenterMap = useCallback(() => {
    if (!mapRef.current || !window.google || !window.google.maps) return;

    const bounds = new window.google.maps.LatLngBounds();
    let hasLocations = false;

    // Add driver location
    if (ambulanceLocation && ambulanceLocation.latitude != null && ambulanceLocation.longitude != null) {
      const lat = Number(ambulanceLocation.latitude);
      const lng = Number(ambulanceLocation.longitude);
      if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng)) {
        bounds.extend(new window.google.maps.LatLng(lat, lng));
        hasLocations = true;
      }
    }

    // Add pickup location
    if (pickupLocation && pickupLocation.latitude != null && pickupLocation.longitude != null) {
      const lat = Number(pickupLocation.latitude);
      const lng = Number(pickupLocation.longitude);
      if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng)) {
        bounds.extend(new window.google.maps.LatLng(lat, lng));
        hasLocations = true;
      }
    }

    // Add dropoff location
    if (dropoffLocation && dropoffLocation.latitude != null && dropoffLocation.longitude != null) {
      const lat = Number(dropoffLocation.latitude);
      const lng = Number(dropoffLocation.longitude);
      if (!isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng)) {
        bounds.extend(new window.google.maps.LatLng(lat, lng));
        hasLocations = true;
      }
    }

    // Fit bounds to show all locations
    if (hasLocations && bounds.getNorthEast()) {
      if (window.google?.maps && mapRef.current) {
        window.google.maps.event.trigger(mapRef.current, 'resize');
      }
      const padding = isFullscreen ? 150 : 120; // More padding in fullscreen
      isProgrammaticMoveRef.current = true;
      mapRef.current.fitBounds(bounds, { padding });
      setTimeout(() => {
        isProgrammaticMoveRef.current = false;
      }, 800);
      
      // Reset user interaction state to allow map to be stable
      setUserInteracting(false);
    }
  }, [ambulanceLocation, pickupLocation, dropoffLocation, isFullscreen]);

  // Listen for fullscreen changes (user can exit with ESC key)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // Keep map tiles rendered when container resizes or scrolls (prevents blank/grey screen)
  useEffect(() => {
    if (!map) return undefined;
    const container = mapContainerRef.current;
    if (!container) return undefined;

    let rafId = null;
    const triggerResize = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (mapRef.current && window.google?.maps) {
          window.google.maps.event.trigger(mapRef.current, 'resize');
        }
      });
    };

    const resizeObserver = new ResizeObserver(() => {
      triggerResize();
    });
    resizeObserver.observe(container);

    window.addEventListener('scroll', triggerResize, { passive: true });
    const scrollParent = container.closest('.overflow-y-auto') || container.closest('main');
    scrollParent?.addEventListener('scroll', triggerResize, { passive: true });

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('scroll', triggerResize);
      scrollParent?.removeEventListener('scroll', triggerResize);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [map]);

  // Always render the map - no error or loading states
  return (
    <div
      ref={mapContainerRef}
      className="relative w-full h-full bg-gray-900"
      style={{ height, willChange: 'transform', transform: 'translateZ(0)' }}
    >
      {/* Map Controls - Top Right */}
      <div className="absolute top-2 right-2 z-[5] flex flex-col gap-2">
        {/* Fullscreen Button */}
        <button
          onClick={toggleFullscreen}
          className="p-2 bg-white hover:bg-gray-50 rounded shadow-lg transition-colors border border-gray-300"
          title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
        >
          {isFullscreen ? (
            <Minimize2 className="h-4 w-4 text-gray-700" />
          ) : (
            <Maximize2 className="h-4 w-4 text-gray-700" />
          )}
        </button>
        
        {/* Recenter Button - Shows when in fullscreen or when user has panned */}
        {(isFullscreen || userInteracting) && (
          <button
            onClick={recenterMap}
            className="p-2 bg-blue-500 hover:bg-blue-600 rounded shadow-lg transition-colors border border-blue-400 animate-pulse"
            title="Recenter map to show all markers"
          >
            <Crosshair className="h-4 w-4 text-white" />
          </button>
        )}
      </div>

      {/* Fullscreen Pan Hint - Shows briefly when entering fullscreen */}
      {isFullscreen && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[5] pointer-events-none">
          <div className="bg-black/70 text-white px-6 py-3 rounded-lg text-center animate-fade-in-out">
            <p className="text-lg font-medium">🖐️ Drag to pan the map</p>
            <p className="text-sm text-gray-300 mt-1">Use the blue button to recenter</p>
          </div>
          <style>{`
            @keyframes fadeInOut {
              0% { opacity: 0; transform: scale(0.9); }
              10% { opacity: 1; transform: scale(1); }
              80% { opacity: 1; transform: scale(1); }
              100% { opacity: 0; transform: scale(0.95); }
            }
            .animate-fade-in-out {
              animation: fadeInOut 3s ease-in-out forwards;
            }
          `}</style>
        </div>
      )}

      {/* Live Location Title with Last Update Indicator - Top Left - RESPONSIVE SIZE */}
      <div className={`absolute z-[5] backdrop-blur-sm rounded-md shadow-lg transition-all duration-300 ${
        locationJustUpdated 
          ? 'bg-green-500/95 border-2 border-green-300 scale-105' 
          : 'bg-white/90 border border-gray-200'
      } ${
        isFullscreen 
          ? 'top-4 left-4 px-4 py-2' 
          : 'top-2 left-2 px-3 py-1.5'
      }`}>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            {ambulanceLocation && (
              <>
                <div className={`w-2 h-2 rounded-full ${locationJustUpdated ? 'bg-white animate-ping' : 'bg-green-500 animate-pulse'}`}></div>
                {locationJustUpdated && (
                  <div className="w-2 h-2 bg-white rounded-full absolute animate-pulse"></div>
                )}
              </>
            )}
            <h3 className={`font-semibold ${locationJustUpdated ? 'text-white' : 'text-gray-900'} ${isFullscreen ? 'text-base' : 'text-sm'}`}>
              {locationJustUpdated ? '📍 Location Updated!' : 'Live Location'}
            </h3>
          </div>
          {ambulanceLocation && lastLocationUpdate && (
            <span className={`${locationJustUpdated ? 'text-white font-medium' : 'text-gray-500'} ${isFullscreen ? 'text-xs' : 'text-[10px]'}`}>
              {locationJustUpdated ? 'Just now' : `${Math.floor((new Date() - lastLocationUpdate) / 1000)}s ago`}
            </span>
          )}
        </div>
      </div>

      <PremiumMapLegend isFullscreen={isFullscreen} />

      {/* Google Maps Container - Only render when API is loaded */}
      {isLoaded && window.google && window.google.maps ? (
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          // Only control center when NOT in fullscreen AND user is NOT interacting
          // This allows free panning in fullscreen mode and when user drags the map
          center={(isFullscreen || userInteracting) ? undefined : mapCenter}
          zoom={(isFullscreen || userInteracting) ? undefined : zoom}
          onLoad={onMapLoad}
          onDragStart={handleMapDragStart}
          onDragEnd={handleMapDragEnd}
          onZoomChanged={() => {
            if (!isProgrammaticMoveRef.current) {
              setUserInteracting(true);
            }
          }}
          options={{
            zoomControl: true,
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: false, // Disabled - using custom fullscreen
            gestureHandling: 'greedy', // Allow all gestures including pan/drag
            disableDefaultUI: false,
            draggable: true, // Explicitly enable dragging
            scrollwheel: true, // Enable scroll wheel zoom
            draggableCursor: 'grab', // Show grab cursor on hover
            draggingCursor: 'grabbing', // Show grabbing cursor while dragging
            styles: uberMapStyle
          }}
        >
          {/* Driver Location Marker - Ambulance SVG Icon (real-time tracking) */}
          {/* Renders driver location from API response: tracking.driverLocation or tracking.ambulanceLocation */}
          {(() => {
            // Check all conditions before rendering
            if (!ambulanceLocation) {
              return null;
            }
            
            if (ambulanceLocation.latitude == null || ambulanceLocation.longitude == null) {
              return null;
            }
            
            if (!isLoaded) {
              return null;
            }
            
            if (!window.google || !window.google.maps) {
              return null;
            }
            
            // Use EXACT coordinates from API response - preserve full precision
            const driverLat = parseFloat(ambulanceLocation.latitude);
            const driverLng = parseFloat(ambulanceLocation.longitude);
            
            // Validate coordinates are valid numbers
            if (isNaN(driverLat) || isNaN(driverLng) || !isFinite(driverLat) || !isFinite(driverLng)) {
              return (
                <Marker
                  key="driver-fallback-marker"
                  position={{ lat: parseFloat(ambulanceLocation.latitude) || 0, lng: parseFloat(ambulanceLocation.longitude) || 0 }}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    fillColor: '#EF4444',
                    fillOpacity: 1,
                    strokeColor: '#FFFFFF',
                    strokeWeight: 3,
                    scale: 15,
                  }}
                  zIndex={1010}
                  visible={true}
                  title="Driver Location"
                />
              );
            }
            
            // Validate coordinate ranges
            if (driverLat < -90 || driverLat > 90 || driverLng < -180 || driverLng > 180) {
              return (
                <Marker
                  key="driver-fallback-marker-range"
                  position={{ lat: driverLat, lng: driverLng }}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    fillColor: '#EF4444',
                    fillOpacity: 1,
                    strokeColor: '#FFFFFF',
                    strokeWeight: 3,
                    scale: 15,
                  }}
                  zIndex={1010}
                  visible={true}
                  title="Driver Location"
                />
              );
            }
            
            const markerPosition = { lat: driverLat, lng: driverLng };

            const ambulanceIconConfig = {
              url: ambulancePng,
              scaledSize: new window.google.maps.Size(80, 80),
              anchor: new window.google.maps.Point(40, 40),
            };

            return (
              <>
                {/* Outer pulsing halo */}
                <Circle
                  center={markerPosition}
                  radius={60}
                  options={{
                    fillColor: '#EF4444',
                    fillOpacity: 0.10,
                    strokeColor: '#EF4444',
                    strokeOpacity: 0.3,
                    strokeWeight: 1.5,
                    zIndex: 1005,
                  }}
                />
                {/* Inner GPS accuracy circle */}
                <Circle
                  center={markerPosition}
                  radius={25}
                  options={{
                    fillColor: '#2563EB',
                    fillOpacity: 0.2,
                    strokeColor: '#2563EB',
                    strokeOpacity: 0.5,
                    strokeWeight: 1.5,
                    zIndex: 1006,
                  }}
                />

                <Marker
                  position={markerPosition}
                  title="Ambulance - Live Location"
                  icon={ambulanceIconConfig}
                  zIndex={1012}
                  optimized={false}
                  visible={true}
                  onLoad={(marker) => {
                    driverMarkerRef.current = marker;
                    console.log('[AmbulanceMarker] Loaded on map at', driverLat, driverLng, 'icon:', ambulancePng?.substring(0, 60));
                  }}
                  onClick={() => {
                    setSelectedMarker('current');
                  }}
                />

                {/* InfoWindow for Driver Location */}
                {selectedMarker === 'current' && (
                  <InfoWindow
                    position={markerPosition}
                    onCloseClick={() => setSelectedMarker(null)}
                  >
                    <div className="p-2">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                        <span className="text-sm font-semibold text-gray-900">Driver Location</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">Live tracking active</p>
                      {ambulanceLocation.lastUpdated && (
                        <p className="text-xs text-gray-500 mt-1">
                          Updated: {new Date(ambulanceLocation.lastUpdated).toLocaleTimeString()}
                        </p>
                      )}
                    </div>
                  </InfoWindow>
                )}
              </>
            );
          })()}

          {/* Pickup & Dropoff markers added imperatively via useEffect above */}

          {/* Route Polyline from Driver to Pickup - COMMENTED OUT - Only show allocated route
          {driverToPickupRoute.length > 0 && window.google && window.google.maps && driverToPickupKey && (
            <>
              <Polyline
                key={`driver-pickup-route-shadow-${driverToPickupKey}`}
                path={driverToPickupRoute.map(point => ({
                  lat: point.lat,
                  lng: point.lng
                }))}
                options={{
                  strokeColor: "#1E3A8A",
                  strokeOpacity: 0.3,
                  strokeWeight: 10,
                  geodesic: true,
                  zIndex: 994,
                  icons: [],
                }}
              />
              <Polyline
                key={`driver-pickup-route-${driverToPickupKey}`}
                path={driverToPickupRoute.map(point => ({
                  lat: point.lat,
                  lng: point.lng
                }))}
                options={{
                  strokeColor: "#60A5FA",
                  strokeOpacity: 0.9,
                  strokeWeight: 6,
                  geodesic: true,
                  zIndex: 997,
                  icons: [
                    {
                      icon: {
                        path: 'M 0,-1 0,1',
                        strokeOpacity: 1,
                        scale: 4,
                      },
                      offset: '0',
                      repeat: '20px',
                    },
                  ],
                }}
              />
            </>
          )}
          */}

          {/* Planned Original Route (RED) — rendered when driver deviates */}
          {plannedRoutePath.length > 0 && routeVersion > 0 && window.google?.maps && routeKey && (
            <>
              <Polyline
                key={`route-planned-glow-${routeKey}-${routeVersion}`}
                path={plannedRoutePath.map(p => ({ lat: p.lat, lng: p.lng }))}
                options={{
                  strokeColor: '#DC2626',
                  strokeOpacity: 0.25,
                  strokeWeight: 7,
                  geodesic: true,
                  zIndex: 992,
                }}
              />
              <Polyline
                key={`route-planned-main-${routeKey}-${routeVersion}`}
                path={plannedRoutePath.map(p => ({ lat: p.lat, lng: p.lng }))}
                options={{
                  strokeColor: '#EF4444',
                  strokeOpacity: 0.85,
                  strokeWeight: 4,
                  geodesic: true,
                  zIndex: 994,
                  icons: [
                    {
                      icon: {
                        path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                        fillColor: '#EF4444',
                        fillOpacity: 1,
                        strokeColor: '#FFFFFF',
                        strokeWeight: 1.2,
                        scale: 2.5,
                      },
                      offset: '25px',
                      repeat: '70px',
                    },
                  ],
                }}
              />
            </>
          )}

          {/* Active / Current Route from Ambulance to Destination (BLUE) */}
          {routePath.length > 0 && window.google && window.google.maps && routeKey && (
            <>
              {/* Outer glow */}
              <Polyline
                key={`route-glow-${routeKey}-${routeVersion}`}
                path={routePath.map(p => ({ lat: p.lat, lng: p.lng }))}
                options={{
                  strokeColor: '#1E40AF',
                  strokeOpacity: 0.25,
                  strokeWeight: 10,
                  geodesic: true,
                  zIndex: 995,
                }}
              />
              {/* Main navigation line */}
              <Polyline
                key={`route-main-${routeKey}-${routeVersion}`}
                path={routePath.map(p => ({ lat: p.lat, lng: p.lng }))}
                options={{
                  strokeColor: '#1A73E8',
                  strokeOpacity: 1,
                  strokeWeight: 5,
                  geodesic: true,
                  zIndex: 998,
                  icons: [
                    {
                      icon: {
                        path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                        fillColor: '#1A73E8',
                        fillOpacity: 1,
                        strokeColor: '#FFFFFF',
                        strokeWeight: 1.5,
                        scale: 3,
                      },
                      offset: '25px',
                      repeat: '65px',
                    },
                  ],
                }}
              />
            </>
          )}
        </GoogleMap>
      ) : (
        // Silent loading state - no error messages shown
        <div className="w-full h-full bg-gray-100" />
      )}
    </div>
  );
};

// Memoize TrackingMap to prevent unnecessary re-renders
// Only re-render when actual location coordinates change, not distance/ETA/status
const MemoizedTrackingMap = React.memo(TrackingMap, (prevProps, nextProps) => {
  // Helper to compare location objects - only care about coordinates
  const locationsEqual = (loc1, loc2) => {
    if (!loc1 && !loc2) return true;
    if (!loc1 || !loc2) return false;
    // Compare coordinates with some tolerance for floating point
    const lat1 = Number(loc1.latitude) || 0;
    const lng1 = Number(loc1.longitude) || 0;
    const lat2 = Number(loc2.latitude) || 0;
    const lng2 = Number(loc2.longitude) || 0;
    // Use a small tolerance for floating point comparison
    const tolerance = 0.0000001;
    return Math.abs(lat1 - lat2) < tolerance && Math.abs(lng1 - lng2) < tolerance;
  };

  // Check if locations changed
  const pickupSame = locationsEqual(prevProps.pickupLocation, nextProps.pickupLocation);
  const dropoffSame = locationsEqual(prevProps.dropoffLocation, nextProps.dropoffLocation);
  const ambulanceSame = locationsEqual(prevProps.ambulanceLocation, nextProps.ambulanceLocation);
  const directionSame = prevProps.journeyDirection === nextProps.journeyDirection;
  
  return pickupSame && dropoffSame && ambulanceSame && directionSame;
});

export default MemoizedTrackingMap;














